export type AudioNoiseSuppressionMode = "none" | "subtle" | "strong";

export const AUDIO_NOISE_SUPPRESSION_KEY = "MediasiteWebRecorderAudioNoiseSuppression";
export const AUDIO_NOISE_SUPPRESSION_SUBTLE_LEVEL_KEY = "MediasiteWebRecorderAudioNoiseSuppressionSubtleLevel";
export const AUDIO_NOISE_SUPPRESSION_STRONG_LEVEL_KEY = "MediasiteWebRecorderAudioNoiseSuppressionStrongLevel";

export const DEFAULT_AUDIO_NOISE_SUPPRESSION_SUBTLE_LEVEL = 18;
export const DEFAULT_AUDIO_NOISE_SUPPRESSION_STRONG_LEVEL = 25;

const clampSuppressionLevel = (value: number) => Math.max(0, Math.min(100, Math.round(value)));

const loadSuppressionLevel = (key: string, fallback: number) => {
  const raw = window.localStorage.getItem(key);
  const parsed = raw == null ? NaN : Number(raw);
  if (!Number.isFinite(parsed)) {
    return fallback;
  }

  return clampSuppressionLevel(parsed);
};

export const isAudioNoiseSuppressionMode = (value: string): value is AudioNoiseSuppressionMode => (
  value === "none" || value === "subtle" || value === "strong"
);

export const loadAudioNoiseSuppressionMode = (): AudioNoiseSuppressionMode => {
  const raw = window.localStorage.getItem(AUDIO_NOISE_SUPPRESSION_KEY) ?? "none";
  return isAudioNoiseSuppressionMode(raw) ? raw : "none";
};

export const saveAudioNoiseSuppressionMode = (mode: AudioNoiseSuppressionMode) => {
  window.localStorage.setItem(AUDIO_NOISE_SUPPRESSION_KEY, mode);
};

export const loadAudioNoiseSuppressionSubtleLevel = () => loadSuppressionLevel(
  AUDIO_NOISE_SUPPRESSION_SUBTLE_LEVEL_KEY,
  DEFAULT_AUDIO_NOISE_SUPPRESSION_SUBTLE_LEVEL,
);

export const loadAudioNoiseSuppressionStrongLevel = () => loadSuppressionLevel(
  AUDIO_NOISE_SUPPRESSION_STRONG_LEVEL_KEY,
  DEFAULT_AUDIO_NOISE_SUPPRESSION_STRONG_LEVEL,
);

export const saveAudioNoiseSuppressionSubtleLevel = (level: number) => {
  window.localStorage.setItem(
    AUDIO_NOISE_SUPPRESSION_SUBTLE_LEVEL_KEY,
    String(clampSuppressionLevel(level)),
  );
};

export const saveAudioNoiseSuppressionStrongLevel = (level: number) => {
  window.localStorage.setItem(
    AUDIO_NOISE_SUPPRESSION_STRONG_LEVEL_KEY,
    String(clampSuppressionLevel(level)),
  );
};

export const isolationValueForMode = (mode: AudioNoiseSuppressionMode): number => {
  switch (mode) {
    case "none": return 0;
    case "subtle": return loadAudioNoiseSuppressionSubtleLevel();
    case "strong": return loadAudioNoiseSuppressionStrongLevel();
  }
};
